import React from 'react';
import Icon from '../../../components/AppIcon';

const InitiativeProgress = ({ title, initiatives }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-success';
      case 'on-track': return 'text-primary';
      case 'at-risk': return 'text-warning';
      case 'delayed': return 'text-error';
      default: return 'text-muted-foreground';
    }
  };

  const getStatusBgColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-success';
      case 'on-track': return 'bg-primary';
      case 'at-risk': return 'bg-warning';
      case 'delayed': return 'bg-error';
      default: return 'bg-muted-foreground';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return 'CheckCircle';
      case 'on-track': return 'Clock';
      case 'at-risk': return 'AlertTriangle';
      case 'delayed': return 'XCircle';
      default: return 'Circle';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'completed': return 'Completed';
      case 'on-track': return 'On Track';
      case 'at-risk': return 'At Risk';
      case 'delayed': return 'Delayed';
      default: return 'Unknown';
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 card-shadow">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-foreground">{title}</h3>
        <div className="text-sm text-muted-foreground">
          {initiatives?.filter(i => i?.status === 'completed')?.length} of {initiatives?.length} completed
        </div>
      </div>
      <div className="space-y-6">
        {initiatives?.map((initiative, index) => (
          <div key={index} className="space-y-3">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-1">
                  <Icon 
                    name={getStatusIcon(initiative?.status)} 
                    size={16} 
                    className={getStatusColor(initiative?.status)}
                  />
                  <h4 className="text-sm font-medium text-foreground">{initiative?.name}</h4>
                </div>
                <p className="text-xs text-muted-foreground mb-2">{initiative?.description}</p>
              </div>
              <div className="text-right ml-4">
                <div className={`text-xs font-medium ${getStatusColor(initiative?.status)}`}>
                  {getStatusLabel(initiative?.status)}
                </div>
                <div className="text-xs text-muted-foreground">
                  Due: {initiative?.dueDate}
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">Progress</span>
                <span className="font-medium text-foreground">{initiative?.progress}%</span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div 
                  className={`h-2 rounded-full transition-all duration-300 ${getStatusBgColor(initiative?.status)}`}
                  style={{ width: `${initiative?.progress}%` }}
                />
              </div>
            </div>

            <div className="flex items-center justify-between text-xs">
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-1">
                  <Icon name="DollarSign" size={12} className="text-muted-foreground" />
                  <span className="text-muted-foreground">Budget: ${initiative?.budget?.toLocaleString()}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Icon name="Users" size={12} className="text-muted-foreground" />
                  <span className="text-muted-foreground">Team: {initiative?.teamSize}</span>
                </div>
              </div>
              <div className="flex items-center space-x-1">
                <Icon name="Target" size={12} className="text-muted-foreground" />
                <span className="text-muted-foreground">ROI: {initiative?.expectedROI}</span>
              </div>
            </div>

            {initiative?.milestones && (
              <div className="bg-muted rounded-lg p-3">
                <div className="text-xs font-medium text-foreground mb-2">Key Milestones</div>
                <div className="space-y-1">
                  {initiative?.milestones?.map((milestone, mIndex) => (
                    <div key={mIndex} className="flex items-center justify-between text-xs">
                      <div className="flex items-center space-x-2">
                        <div className={`w-2 h-2 rounded-full ${milestone?.completed ? 'bg-success' : 'bg-border'}`} />
                        <span className={milestone?.completed ? 'text-foreground' : 'text-muted-foreground'}>
                          {milestone?.name}
                        </span>
                      </div>
                      <span className="text-muted-foreground">{milestone?.date}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default InitiativeProgress;